import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  getStoredPreviewEmailOverride,
  PREVIEW_EMAIL_OVERRIDE_HEADER,
  requestPreviewEmailOverrideForMissingError,
} from "@/utils/previewEmailOverride";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("utils/apiClient");

interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const resolveApiErrorMessage = (args: {
  data: unknown;
  status: number;
  statusText: string;
}): string => {
  if (isRecord(args.data)) {
    const canonicalMessage = normalizeString(args.data.message);
    if (canonicalMessage) {
      return canonicalMessage;
    }
  }

  return `HTTP ${args.status}: ${args.statusText}`;
};

/**
 * Authenticated JSON client for legacy admin flows that have not moved to RTK Query yet.
 *
 * @remarks
 * Each request carries the current Firebase ID token, a correlation id, and
 * the preview email override header so backend logging and safe-mode email
 * enforcement stay aligned with the main API layer.
 */
export class ApiClient {
  private static baseUrl = import.meta.env.VITE_API_URL;

  /**
   * Sends a JSON request to the configured API base URL.
   *
   * @throws Error when the operator is not authenticated or the backend
   * returns a user-visible error message.
   */
  static async request(endpoint: string, options: ApiRequestOptions = {}) {
    const { method = "GET", body, headers = {} } = options;
    const correlationId =
      headers[CORRELATION_ID_HEADER] ?? generateCorrelationId();
    const requestLogger = logger.withCorrelationId(correlationId);

    const currentUser = memoizedAuth.getCurrentUser();
    if (!currentUser) {
      throw new Error("Not authenticated");
    }

    const idToken = await currentUser.getIdToken();

    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...headers,
      [CORRELATION_ID_HEADER]: correlationId,
    };

    const previewEmailOverride = getStoredPreviewEmailOverride();
    if (
      previewEmailOverride &&
      !requestHeaders[PREVIEW_EMAIL_OVERRIDE_HEADER]
    ) {
      requestHeaders[PREVIEW_EMAIL_OVERRIDE_HEADER] = previewEmailOverride;
    }

    const fetchOptions: RequestInit = {
      method,
      headers: requestHeaders,
    };

    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, fetchOptions);

    if (!response.ok) {
      requestLogger.error(
        `API request failed: ${method} ${endpoint}`,
        undefined,
        {
          operation: "api_request_failed",
          method,
          endpoint,
          status: response.status,
        },
      );

      const errorData: unknown = await response.json().catch(() => ({}));
      const message = resolveApiErrorMessage({
        data: errorData,
        status: response.status,
        statusText: response.statusText,
      });
      requestPreviewEmailOverrideForMissingError({
        data: errorData,
        message,
      });
      throw new Error(message);
    }

    return response.json();
  }
  static get(endpoint: string, headers?: Record<string, string>) {
    return this.request(endpoint, {
      method: "GET",
      ...(headers && { headers }),
    });
  }

  static post(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
  ) {
    return this.request(endpoint, {
      method: "POST",
      body,
      ...(headers && { headers }),
    });
  }

  static put(
    endpoint: string,
    body?: unknown,
    headers?: Record<string, string>,
  ) {
    return this.request(endpoint, {
      method: "PUT",
      body,
      ...(headers && { headers }),
    });
  }

  static delete(endpoint: string, headers?: Record<string, string>) {
    return this.request(endpoint, {
      method: "DELETE",
      ...(headers && { headers }),
    });
  }
}
